import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import API from "../../../services";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

type WlTransactionRecord = {
  referenceId: string;
  utr: string;
  amount: number;
  point: number;
  currency?: string;
  transactionMethod: string;
  status: string;
  createdAt: number;
};

type GetWlTransactionsResp = {
  status?: string;
  errorDescription?: string;
  count?: number;
  transactions?: WlTransactionRecord[];
};

const PAGE_SIZE = 20;

const formatDateTime = (epochMillis: number): string => {
  if (!Number.isFinite(epochMillis)) return "-";
  const date = new Date(epochMillis);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) return "-";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

const maskReferenceId = (value: string): string => {
  const raw = String(value || "").trim();
  if (!raw) return "-";
  if (raw.length <= 8) return raw;
  return `${raw.slice(0, 4)}...${raw.slice(-4)}`;
};

const formatAmountWithCurrency = (amount: number, currency?: string): string => {
  const formattedAmount = formatNumber(amount);
  const ccy = String(currency || "").trim() || "INR";
  return `${formattedAmount} ${ccy}`;
};

const statusPillClass = (statusRaw: string): string => {
  const status = String(statusRaw || "").trim().toLowerCase();
  if (status.includes("success")) {
    return "border-emerald-400/35 bg-emerald-500/12 text-emerald-300";
  }
  if (status.includes("pending")) {
    return "border-amber-400/35 bg-amber-500/12 text-amber-300";
  }
  if (status.includes("abandon") || status.includes("fail")) {
    return "border-rose-400/35 bg-rose-500/12 text-rose-300";
  }
  return "border-white/20 bg-white/5 text-zinc-200";
};

const WhitelabelTransactions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<WlTransactionRecord[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [expandedId, setExpandedId] = useState<string>("");
  const observerRef = useRef<IntersectionObserver | null>(null);

  const whitelabelId = useMemo(() => {
    const fromState = String(
      (location.state as { whitelabelId?: string } | null)?.whitelabelId || ""
    ).trim();
    const fromQuery = String(searchParams.get("whitelabelId") || "").trim();
    return fromState || fromQuery;
  }, [location.state, searchParams]);

  const copyText = async (value: string, label: string) => {
    const text = String(value || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error(`Unable to copy ${label.toLowerCase()}.`);
    }
  };

  const toggleExpanded = (row: WlTransactionRecord) => {
    const key = `${row.referenceId || "na"}-${row.createdAt || 0}`;
    setExpandedId((prev) => (prev === key ? "" : key));
  };

  const loadTransactions = useCallback(async () => {
    if (!whitelabelId || !hasMore) return;
    setLoading(true);
    try {
      const response = await API.post<GetWlTransactionsResp>(
        "/whitelabel/get-transactions",
        {
          whitelabelId,
          page,
          pageSize: PAGE_SIZE,
        }
      );
      const data = response?.data;
      if (data?.status !== "RS_OK") {
        const msg = data?.errorDescription || "Unable to fetch transaction history.";
        toast.error(msg);
        setHasMore(false);
        return;
      }

      const nextRows = Array.isArray(data?.transactions) ? data.transactions : [];
      setRows((prev) => (page === 1 ? nextRows : [...prev, ...nextRows]));
      setHasMore(nextRows.length === PAGE_SIZE);
      setExpandedId("");
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { errorDescription?: string } } })?.response?.data
          ?.errorDescription || "Unable to fetch transaction history.";
      toast.error(msg);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [hasMore, page, whitelabelId]);

  useEffect(() => {
    setRows([]);
    setPage(1);
    setHasMore(true);
    setExpandedId("");
    setLoading(false);
  }, [whitelabelId]);

  useEffect(() => {
    if (!whitelabelId) {
      setRows([]);
      setHasMore(false);
      setLoading(false);
      return;
    }
    loadTransactions();
  }, [loadTransactions, whitelabelId, page]);

  useEffect(() => {
    const node = document.getElementById("wl-transactions-sentinel");
    if (!node) return;

    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading) {
          setPage((prev) => prev + 1);
        }
      },
      { root: null, rootMargin: "100px", threshold: 0 }
    );
    observerRef.current.observe(node);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasMore, loading]);

  return (
    <div className="px-3 sm:px-4 py-[5rem] pb-[8rem] text-white">
      <div className="mx-auto max-w-6xl">
        <header className="mb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="bg-inputbox-color flex h-8 w-8 shrink-0 items-center justify-center rounded"
            aria-label="Go back"
            title="Back"
          >
            <ArrowBackIcon className="text-white" style={{ width: "20px", height: "20px" }} />
          </button>
          <h1 className="text-2xl font-semibold leading-tight text-zinc-100 sm:text-3xl">
            Transaction history
          </h1>
        </header>

        {!whitelabelId ? (
          <div className="rounded-lg border border-amber-500/35 bg-amber-950/20 p-4 text-amber-200/95">
            Whitelabel ID is missing. Open this page from whitelabel details.
          </div>
        ) : (
          <div>
            {loading ? <p className="text-sm text-zinc-400">Loading transactions...</p> : null}
            {!loading && rows.length === 0 ? (
              <p className="text-sm text-zinc-400">No transactions found.</p>
            ) : null}

            {!loading && rows.length > 0 ? (
              <>
                <div className="space-y-2.5">
                  {rows.map((row) => {
                    const key = `${row.referenceId || "na"}-${row.createdAt || 0}`;
                    const expanded = expandedId === key;
                    return (
                      <div
                        key={key}
                        className="overflow-hidden rounded-xl border border-white/[0.1] bg-[color-mix(in_srgb,var(--border-color)_16%,transparent)]"
                      >
                        <button
                          type="button"
                          onClick={() => toggleExpanded(row)}
                          className="w-full px-3 py-2.5 text-left hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/35"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-zinc-100">
                                <span className="tabular-nums">
                                  {formatAmountWithCurrency(row.amount, row.currency)}
                                </span>
                              </p>
                              <p className="mt-0.5 text-sm text-zinc-400">
                                <span className="tabular-nums">{formatNumber(row.point)}</span> points
                              </p>
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="text-xs text-zinc-300">{formatDateTime(row.createdAt)}</p>
                              <div className="mt-1 flex items-center justify-end gap-2">
                                <span
                                  className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${statusPillClass(
                                    row.status
                                  )}`}
                                >
                                  {row.status || "-"}
                                </span>
                                <ExpandMoreIcon
                                  sx={{
                                    fontSize: 18,
                                    color: "rgba(244, 244, 245, 0.85)",
                                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.2s ease",
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </button>
                        {expanded ? (
                          <div className="border-t border-white/[0.08] px-3 py-2.5 text-xs sm:text-sm">
                            <div className="grid grid-cols-[6rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2">
                              <span className="text-zinc-400">Method</span>
                              <span className="text-zinc-200">{row.transactionMethod || "-"}</span>

                              <span className="text-zinc-400">UTR</span>
                              <div className="flex min-w-0 items-center gap-1.5 text-zinc-200">
                                <span className="truncate">{row.utr || "-"}</span>
                                {row.utr ? (
                                  <button
                                    type="button"
                                    onClick={() => copyText(row.utr, "UTR")}
                                    aria-label="Copy UTR"
                                    title="Copy UTR"
                                    className="shrink-0 rounded p-0.5 text-zinc-400 hover:bg-white/10 hover:text-zinc-100"
                                  >
                                    <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />
                                  </button>
                                ) : null}
                              </div>

                              <span className="text-zinc-400">Reference ID</span>
                              <div className="flex min-w-0 items-center gap-1.5 text-zinc-200">
                                <span className="truncate">{maskReferenceId(row.referenceId)}</span>
                                {row.referenceId ? (
                                  <button
                                    type="button"
                                    onClick={() => copyText(row.referenceId, "Reference ID")}
                                    aria-label="Copy reference ID"
                                    title="Copy reference ID"
                                    className="shrink-0 rounded p-0.5 text-zinc-400 hover:bg-white/10 hover:text-zinc-100"
                                  >
                                    <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <div id="wl-transactions-sentinel" className="h-2 w-full" />
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhitelabelTransactions;
